import { toast } from 'react-toastify'

import axios, { AxiosError } from 'axios'
import {
  GROUP_UNAVAILABLE_CODE,
  leaveUnavailableGroup
} from 'lib/merchant-group'
import { GetServerSidePropsContext, PreviewData } from 'next'
import { parseCookies, setCookie } from 'nookies'
import { ParsedUrlQuery } from 'querystring'

import decodeBase64 from 'utils/decode-base64-to-string'
import { destroySession } from 'utils/DestroySession'
import encodeBase64 from 'utils/encode-to-base64'
import { generateFingerprintToken } from 'utils/fingerprint'
import {
  CAPABILITIES_SYNC_EVENT,
  CREATION_BLOCKED_SYNC_EVENT,
  PLAN_UPGRADE_REQUIRED_CODE,
  PLAN_UPGRADE_REQUIRED_EVENT
} from 'utils/plan-upgrade-event'
import { SESSION_COOKIE_OPTIONS } from 'utils/sessionCookie'
import { track } from 'utils/tracking'

import { getAPIClient, RequestScope } from './axios'
import { requestBlocker } from './requestBlocker'

/**
 * SC-10694: agrupa a URL para caber como propriedade de evento sem vazar id.
 * `/v1/customers/9f3a-...` vira `/v1/customers/:id`, senão cada requisição
 * geraria um valor único e o breakdown por endpoint seria inútil.
 */
const normalizeUrl = (url: string): string =>
  url
    .split('?')[0]
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '/:id'
    )
    .replace(/\/\d+/g, '/:id')

export type DataServiceProps = {
  ctx?: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>
  type?: 'POST' | 'GET' | 'DELETE' | 'PATCH' | 'PUT'
  data?: any
  url: string
} & RequestScope

export type Response<T = any> = {
  data: T
  error: boolean | string
  detail_error: string | string[]
  message: string
  status: number
}

export const getClientIP = async (): Promise<string> => {
  try {
    const response = await axios.get('https://api.ipify.org?format=json')
    return response.data.ip
  } catch (error) {
    return ''
  }
}

const getFingerprintToken = async (): Promise<string> => {
  if (typeof window !== 'undefined') {
    try {
      const token = await generateFingerprintToken()
      return token || ''
    } catch (error) {
      return ''
    }
  }
  return ''
}

export const DataService = async <T = any>({
  ctx = undefined,
  type = 'POST',
  data,
  url,
  scope,
  merchantUuid
}: DataServiceProps): Promise<Response<T> | any> => {
  if (['POST', 'PATCH', 'PUT'].includes(type)) {
    const requestKey = `${type}:${url}`

    if (requestBlocker.isBlocked(requestKey)) {
      return {
        data: { records: {}, statistics: {} },
        error: true,
        message: 'Por favor, aguarde um instante para tentar novamente.',
        status: 429,
        detail_error: 'Por favor, aguarde um instante para tentar novamente.'
      }
    }

    requestBlocker.block(requestKey, 1000)
  }

  const client = getAPIClient(ctx, { scope, merchantUuid })
  let ip = ''
  const { ['nextauth.id']: value } = parseCookies(ctx)
  ip = value ? decodeBase64(value) : ''
  if (!ip) {
    ip = await getClientIP()
    const encryptIp = encodeBase64(ip)
    setCookie(undefined, 'nextauth.id', encryptIp, SESSION_COOKIE_OPTIONS)
  }

  const headers: Record<string, string> = {
    'X-Forwarded-For': ip
  }

  // Generate fingerprint before making the API call
  if (typeof window !== 'undefined') {
    const fingerprintToken = await getFingerprintToken()
    if (fingerprintToken) {
      headers['X-Fingerprint-Token'] = fingerprintToken
    }
  }

  try {
    const response = await client({
      data,
      method: type,
      url,
      headers: headers
    })

    if (response.data?.token && url === '/v2/sign_in') {
      client.defaults.headers.common.Authorization = `Bearer ${response.data.token}`
    }

    // Sync subdomain cookie from merchants/perfil response
    if (
      type === 'GET' &&
      url.includes('/v1/merchants/perfil') &&
      typeof window !== 'undefined'
    ) {
      const record = response.data?.record || response.data
      const subdomain = record?.subdomain
      if (subdomain && subdomain !== 'null') {
        const { ['nextauth.subdomain']: current } = parseCookies()
        if (current !== subdomain) {
          setCookie(
            undefined,
            'nextauth.subdomain',
            subdomain,
            SESSION_COOKIE_OPTIONS
          )
        }
      }

      // Mantém o bloqueio de criação em dia sem depender do JWT (SC-10363): o
      // token dura 7 dias, então quem assina um plano continuaria vendo o
      // banner e o modal de upgrade até o próximo login. O perfil é a fonte
      // fresca — quando ela diverge, avisa a aplicação.
      if (typeof record?.creation_blocked === 'boolean') {
        window.dispatchEvent(
          new CustomEvent(CREATION_BLOCKED_SYNC_EVENT, {
            detail: { creationBlocked: record.creation_blocked }
          })
        )
      }

      // Mesmo problema do bloqueio acima, agora para os acessos do plano
      // (SC-10878): o claim `capabilities` do JWT só é reemitido no login, então
      // quem fazia upgrade só via o recurso liberado no próximo acesso. O
      // perfil traz as capabilities atuais e a aplicação se atualiza sozinha.
      if (record?.capabilities && typeof record.capabilities === 'object') {
        window.dispatchEvent(
          new CustomEvent(CAPABILITIES_SYNC_EVENT, {
            detail: { capabilities: record.capabilities }
          })
        )
      }
    }

    return response
  } catch (err) {
    const errors = err as Error | AxiosError
    if (axios.isAxiosError(errors) && errors.response) {
      // SC-10999: grupo que deixou de valer não desloga; volta para a empresa
      if (
        errors.response.status === 409 &&
        errors.response.data?.code === GROUP_UNAVAILABLE_CODE &&
        !ctx
      ) {
        leaveUnavailableGroup(errors.response.data?.message)
        // A página recarrega em seguida: a chamada não resolve, para nenhuma
        // tela tentar ler a resposta de erro como se fosse a lista
        return new Promise(() => undefined)
      }

      if (errors.response.status === 401) {
        destroySession(ctx)
        if (!ctx && url !== '/v2/sign_in') {
          window.location.replace('/')
        }
      }

      if (errors.response.status === 403 && !ctx) {
        // Bloqueio de criação por plano (SC-10363): em vez do toast genérico de
        // permissão, avisa a aplicação para abrir o modal de upgrade. Rede de
        // segurança para qualquer ação de criar que não passe pelo CreateGate.
        if (errors.response.data?.code === PLAN_UPGRADE_REQUIRED_CODE) {
          window.dispatchEvent(
            new CustomEvent(PLAN_UPGRADE_REQUIRED_EVENT, {
              detail: {
                resourceName: errors.response.data?.resource_name,
                plan: errors.response.data?.suggested_plan
              }
            })
          )
        } else {
          const message =
            errors.response.data?.message ||
            'Sem permissão para realizar esta ação.'
          toast.error(message, { toastId: 'permission-denied' })
        }
      }

      // SC-10694: ponto único de falha de API do produto todo. Antes, só o
      // cadastro tinha tracking de erro — nenhuma outra tela deixava rastro
      // quando quebrava (e não há Sentry no frontend). O 401 fica de fora:
      // expiração de sessão é rotina e só geraria ruído.
      if (typeof window !== 'undefined' && errors.response.status !== 401) {
        track('api_error', {
          endpoint: normalizeUrl(url),
          method: type,
          status: errors.response.status,
          code: errors.response.data?.code,
          // Mensagem cortada: serve para agrupar, não para ler log completo.
          message: String(
            errors.response.data?.message ||
              errors.response.data?.detail_error ||
              ''
          ).slice(0, 120)
        })
      }

      return {
        data: { records: {}, statistics: {} },
        ...(!!errors.response?.data?.detail_error && {
          detail_error:
            typeof errors.response?.data?.detail_error === 'string'
              ? errors.response?.data?.detail_error
              : errors.response?.data?.detail_error[0]
        }),
        error: errors.response.data?.error,
        message: errors.response.data?.message,
        status: errors.response.status
      }
    }

    // Check for network errors (no internet connection, server unreachable, etc.)
    const isNetworkError =
      errors.message === 'Network Error' ||
      (axios.isAxiosError(errors) && errors.code === 'ERR_NETWORK')

    if (isNetworkError) {
      // Status 0 distingue queda de conexão de erro devolvido pela API: sem
      // isso, instabilidade de rede do cliente viraria "bug do produto".
      if (typeof window !== 'undefined') {
        track('api_error', {
          endpoint: normalizeUrl(url),
          method: type,
          status: 0,
          code: 'ERR_NETWORK'
        })
      }

      return {
        error: true,
        message: 'Falha na conexão. Verifique sua internet e tente novamente.',
        detail_error: '',
        status: 0
      }
    }

    return { error: true, message: errors.message }
  }
}
